import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import type { MeResponse } from "@starsach/shared-types";
import { useAuth } from "../auth/AuthProvider";
import { StepUpProvider } from "../auth/StepUpProvider";
import { BackToTop } from "../components/BackToTop";
import { PoweredBy, StarsachWordmark } from "../components/Brand";
import { NudgeBanner } from "../components/NudgeBanner";
import { Dialog, btnPrimary, btnSecondary, sentence, vtag } from "../components/ui";
import { actsAsCustomer, modulePath, visibleModules, type Module } from "../lib/modules";
import { useUiStore } from "../store/ui";
import { setViewAsMode } from "../viewAs";

/*
 * The shell (section 8.9, mock-ups of 2026-09-18): a left rail of modules
 * in three groups, the active module expanded to its sections, identity and
 * version at the foot of the rail. Content fills the rest. Under lg the
 * rail is a drawer behind a menu button.
 */
const navItem = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-9 items-center gap-2.5 rounded-lg px-3 text-sm transition-colors ${isActive ? "bg-accent-soft font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "text-neutral-700 hover:bg-neutral-100 hover:text-ink dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white"}`;
const navSub = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-8 items-center rounded-lg py-1 pl-9 pr-3 text-[13px] transition-colors ${isActive ? "bg-accent-soft/70 font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "text-neutral-600 hover:bg-neutral-100 hover:text-ink dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"}`;
const navGroup = "px-3 pb-1.5 pt-5 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-500";

const ICONS: Record<Module["id"] | "docs", ReactNode> = {
  focus: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /></>,
  alarms: <><path d="M6 8a6 6 0 0112 0v5l2 3H4l2-3V8z" /><path d="M10 19a2 2 0 004 0" /></>,
  journeys: <><path d="M4 19V9l8-5 8 5v10" /><path d="M9 19v-6h6v6" /></>,
  "flow-configuration": <path d="M4 6h16M4 12h10M4 18h7" />,
  quotas: <><path d="M12 3l9 4-9 4-9-4 9-4z" /><path d="M3 12l9 4 9-4" /></>,
  "trusted-advisor": <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />,
  support: <path d="M4 5h16v11H8l-4 4V5z" />,
  access: <><circle cx="9" cy="8" r="3.5" /><path d="M2 20c0-4 3-6 7-6s7 2 7 6" /><path d="M17 9a3 3 0 010 6M21 20c0-3-2-5-4-5.5" /></>,
  audit: <><path d="M5 4h14v16H5z" /><path d="M9 9h6M9 13h6M9 17h3" /></>,
  customers: <><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-8h6v8" /></>,
  platform: <path d="M4 18V6M10 18v-8M16 18V9M22 18H2" />,
  docs: <><path d="M4 4h16v16H4z" /><path d="M8 9h8M8 13h5" /></>,
};

function Icon({ id }: { id: keyof typeof ICONS }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 flex-none stroke-current" fill="none" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {ICONS[id]}
    </svg>
  );
}

/** The environment in the current address, if the page has one; the nav opens the next module on it. */
function useCurrentEnvironment(): string | undefined {
  const { environmentId } = useParams();
  const last = useUiStore((s) => s.lastEnvironmentId);
  return environmentId ?? last;
}

function SideNav({ me, uiVersion, onNavigate, signOut }: { me: MeResponse | undefined; uiVersion: string; onNavigate: () => void; signOut: () => void }) {
  const { pathname } = useLocation();
  const { preferences } = useAuth();
  const environmentId = useCurrentEnvironment();
  const modules = visibleModules(me);
  const groups: Array<{ id: Module["group"]; label?: string }> = [{ id: "modules" }, { id: "organization", label: "Organization" }, { id: "gairm", label: "Gairm" }];
  return (
    <nav aria-label="Main" className="flex h-full flex-col gap-0.5 px-3 py-4">
      <Link to="/" onClick={onNavigate} className="mb-3 px-3 py-1.5">
        <StarsachWordmark className="text-base text-ink dark:text-white" />
      </Link>
      {groups.map((g) => {
        const items = modules.filter((m) => m.group === g.id);
        if (items.length === 0) return null;
        return (
          <div key={g.id} className="contents">
            {g.label && <div className={navGroup}>{g.label}</div>}
            {items.map((m) => {
              const active = m.path === "/" ? pathname === "/" : pathname === m.path || pathname.startsWith(`${m.path}/`);
              // A focus environment set for the module wins (section 13.1); otherwise staff open a cross-customer
              // module on its every-customer page and everyone else lands on the environment last looked at.
              const focus = preferences.focusEnvironments[m.id];
              const to = focus ? modulePath(m, focus) : m.crossCustomer && !actsAsCustomer(me) ? m.path : modulePath(m, environmentId);
              return (
                <div key={m.id} className="contents">
                  <NavLink to={to} end={m.path === "/"} className={navItem} onClick={onNavigate}>
                    <Icon id={m.id} />
                    {m.label}
                  </NavLink>
                  {active && m.sections && (
                    <ul aria-label={`${m.label} sections`} className="flex flex-col gap-0.5">
                      {m.sections.map((sct) => (
                        <li key={sct.id}>
                          <NavLink to={modulePath(m, environmentId, sct.id)} className={navSub} onClick={onNavigate}>
                            {sct.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="flex-1" />
      <NavLink to="/docs" className={navItem} onClick={onNavigate}>
        <Icon id="docs" />
        Documentation
      </NavLink>
      <div className="mt-2 border-t border-neutral-200 px-3 pt-3 text-xs text-neutral-600 dark:border-neutral-800 dark:text-neutral-400">
        {/* Section 13: the identity is the way into Account. */}
        <Link to="/account" onClick={onNavigate} className="block truncate hover:text-ink hover:underline dark:hover:text-white" aria-label="Account">
          <span className="font-medium text-ink dark:text-white">{me?.displayName ?? me?.email}</span>
          <span className="block">{me?.viewAs ? "Gairm staff" : me?.role && sentence(me.role)}</span>
        </Link>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span>
            {vtag(uiVersion)}
            {me?.portalVersion && me.portalVersion !== uiVersion ? ` (API ${vtag(me.portalVersion)})` : ""}
          </span>
          <button type="button" onClick={signOut} className="rounded-md border border-neutral-300 px-2 py-1 font-medium text-ink hover:bg-neutral-100 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-800">
            Sign out
          </button>
        </div>
      </div>
    </nav>
  );
}


/**
 * ADR-0011: the one thing a view-as tab shows that the customer's own tab
 * does not. Everything below it is the customer's portal, served by the
 * customer's own routes; the banner names whose, and whether it can be
 * changed from here. A mode change reloads the tab so nothing cached
 * under the old mode survives.
 */
function ViewAsBanner({ viewAs }: { viewAs: NonNullable<MeResponse["viewAs"]> }) {
  const [confirming, setConfirming] = useState(false);
  const name = viewAs.organisationName ?? viewAs.organisationId;
  const switchTo = (mode: "read" | "write") => {
    setViewAsMode(viewAs.organisationId, mode);
    window.location.reload();
  };
  return (
    <div role="region" aria-label="Viewing a customer's portal" className="border-b border-brand-text/30 bg-brand/10 text-sm text-neutral-900 dark:text-neutral-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <span>
          Viewing <strong>{name}</strong>'s portal as Gairm staff ·{" "}
          {viewAs.mode === "write" ? <strong className="text-brand-text">editing enabled</strong> : <span>read-only</span>}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {viewAs.mode === "write" ? (
            <button type="button" onClick={() => switchTo("read")} className={`${btnSecondary} min-h-8 px-3 text-xs`}>
              Back to read-only
            </button>
          ) : (
            <button type="button" onClick={() => setConfirming(true)} className={`${btnSecondary} min-h-8 px-3 text-xs`}>
              Enable editing
            </button>
          )}
          <a href="/customers" className="text-xs underline hover:text-neutral-700 dark:hover:text-neutral-300">
            Exit
          </a>
        </span>
      </div>
      {confirming && (
        <Dialog title={`Edit ${name}'s portal?`} onClose={() => setConfirming(false)}>
          <p className="text-sm">
            You will be able to change anything their own manager could. Every change is recorded in their audit log under your name, and they
            can see it. Nothing happens until you save something.
          </p>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => switchTo("write")} className={btnPrimary}>
              Enable editing
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={btnSecondary}>
              Cancel
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export function AppLayout() {
  const { me, signOut } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Root package.json version, injected by vite.config.ts; the API serves its own on /v1/me.
  const uiVersion = import.meta.env.VITE_PORTAL_VERSION ?? "dev";
  useEffect(() => setOpen(false), [pathname]);
  const close = () => setOpen(false);
  return (
    <StepUpProvider>
    <div className="min-h-screen bg-canvas text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-40 focus:rounded focus:bg-white focus:p-2">
        Skip to content
      </a>
      {me?.viewAs && <ViewAsBanner viewAs={me.viewAs} />}
      {/* Small screens: a bar with the wordmark and a menu button; the rail becomes a drawer. */}
      <header className="flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-2 lg:hidden dark:border-neutral-800 dark:bg-neutral-900">
        <StarsachWordmark className="text-base text-ink dark:text-white" />
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="side-nav" className={`${btnSecondary} min-h-8 px-3 text-xs`}>
          {open ? "Close menu" : "Menu"}
        </button>
      </header>
      <div className="flex min-h-screen">
        {open && <div className="fixed inset-0 z-20 bg-neutral-900/40 lg:hidden" aria-hidden="true" onClick={close} />}
        {/* The rail stays put and scrolls on its own, so Docs and the identity are always at its foot. */}
        <aside id="side-nav" className={`fixed inset-y-0 left-0 z-30 w-60 flex-none overflow-y-auto border-r border-neutral-200 bg-white transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 dark:border-neutral-800 dark:bg-neutral-900 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <SideNav me={me} uiVersion={uiVersion} onNavigate={close} signOut={() => void signOut()} />
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <NudgeBanner />
          <main id="main" className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 lg:px-10">
            <Outlet />
          </main>
          <footer className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-neutral-500 lg:px-10 dark:text-neutral-400">
            <span>
              Starsach Portal {vtag(uiVersion)}
              {me?.portalVersion && me.portalVersion !== uiVersion ? ` (API ${vtag(me.portalVersion)})` : ""}
              {" · "}
              <Link to={`/docs/release-notes#${vtag(uiVersion)}`} className="text-accent-strong underline hover:text-accent dark:text-accent">
                Release notes
              </Link>
              {" · "}
              <Link to="/docs/what-leaves-your-account" className="text-accent-strong underline hover:text-accent dark:text-accent">
                What leaves your account
              </Link>
              {" · "}
              <Link to="/docs" className="text-accent-strong underline hover:text-accent dark:text-accent">
                Documentation
              </Link>
            </span>
            <PoweredBy />
          </footer>
        </div>
      </div>
      <BackToTop />
    </div>
    </StepUpProvider>
  );
}
